import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import BookingForm from '../components/BookingForm';
import LargeGroupModal from '../components/LargeGroupModal';
import Navigation from '../components/Navigation';
import { SITE_URL } from '../config/seo-data';

const StandaloneBooking = () => {
  // Modal states for large group booking
  const [showLargeGroupModal, setShowLargeGroupModal] = useState(false);
  const [largeGroupCount, setLargeGroupCount] = useState(0);

  return (
    <>
      <Helmet>
        <title>Book Corfu Transfer Online | Instant Price & Confirmation - Lasmari Chauffeurs</title>
        <meta name="description" content="Book your Corfu airport transfer online in 60 seconds. Instant fixed pricing, immediate confirmation, free cancellation. Transfers to Kassiopi, Sidari, Paleokastritsa, Gouvia & all Corfu destinations." />
        <meta name="keywords" content="book corfu transfer, corfu transfer booking, corfu airport transfer booking, book corfu taxi online, corfu transfer online booking, corfu airport transfer online, book corfu airport transfer" />

        {/* Canonical URL for this specific page */}
        <link rel="canonical" href={`${SITE_URL}/booking`} />

        {/* Open Graph for social media */}
        <meta property="og:title" content="Book Corfu Transfer Online | Lasmari Chauffeurs" />
        <meta property="og:description" content="Book your Corfu airport transfer online. Instant fixed pricing, free cancellation. All Corfu destinations." />
        <meta property="og:url" content={`${SITE_URL}/booking`} />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:title" content="Book Corfu Transfer Online | Lasmari Chauffeurs" />
        <meta name="twitter:description" content="Book your Corfu airport transfer online. Instant pricing, free cancellation. All destinations." />

        {/* Structured Data for Booking Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Book Your Transfer",
            "description": "Book your Corfu airport transfer with Lasmari Chauffeurs",
            "url": `${SITE_URL}/booking`,
            "mainEntity": {
              "@type": "Service",
              "name": "Corfu Airport Transfer Booking",
              "provider": {
                "@type": "TransportationCompany",
                "name": "Lasmari Chauffeurs",
                "url": SITE_URL
              }
            }
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-[#faf7f6]">
      {/* Main site navigation */}
      <Navigation />

      {/* Booking Form Section */}
      <main className="relative">
        {/* Background photo (natural, unfiltered) with a legibility scrim on top */}
        <div
          className="fixed inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/backgrounds/Visit-Kassiopi-Homepage-Hero.webp')` }}
        />
        <div className="fixed inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/55" />

        {/* Content (top padding clears the fixed navbar) */}
        <div className="relative z-10 pt-32 lg:pt-40 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Page Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-8"
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', textShadow: '0 2px 16px rgba(0,0,0,0.5)' }}>
                Book Your <span className="italic">Transfer</span>
              </h1>
              <p className="text-lg text-white/85 max-w-2xl mx-auto" style={{ textShadow: '0 1px 10px rgba(0,0,0,0.5)' }}>
                Experience premium transportation along the stunning northeastern Corfu
              </p>
            </motion.div>

            {/* Booking Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <BookingForm 
                setShowLargeGroupModal={setShowLargeGroupModal}
                setLargeGroupCount={setLargeGroupCount}
              />
            </motion.div>
          </div>
        </div>
      </main>

      {/* Large Group Modal */}
      <LargeGroupModal 
        isOpen={showLargeGroupModal}
        onClose={() => setShowLargeGroupModal(false)}
        passengerCount={largeGroupCount}
      />
      </div>
    </>
  );
};

export default StandaloneBooking; 